import React, { useState, useEffect } from "react";
import Layout from "../../components/layout/Layout";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { discoveryBadgesApi } from "../../api/discoveryBadges";
import { nowMealsApi } from "../../api/nowMeals";
import { uploadApi } from "../../api/upload";
import Swal from "sweetalert2";

interface Badge {
  _id: string;
  name: string;
  code: string;
  description?: string;
  icon: string;
  displayOrder: number;
  isActive: boolean;
  ruleType: "manual" | "dynamic";
  dynamicType: "none" | "top_picks" | "newly_added" | "high_protein" | "order_again";
  meals: any[];
  poster?: string;
  iconUrl?: string;
  tagline1?: string;
  tagline2?: string;
}

const DiscoveryBadges = () => {
  const [badges, setBadges] = useState<Badge[]>([]);
  const [nowMeals, setNowMeals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [openMealsModal, setOpenMealsModal] = useState(false);
  const [editingBadge, setEditingBadge] = useState<Badge | null>(null);
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [selectedMealToAdd, setSelectedMealToAdd] = useState("");
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);

  const handlePosterUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setUploadingPoster(true);
    try {
      const response = await uploadApi.uploadImage(file, "badges/posters");
      setFormData((prev) => ({ ...prev, poster: response.imageUrl }));
      Swal.fire("Uploaded", "Poster uploaded successfully!", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to upload poster", "error");
    } finally {
      setUploadingPoster(false);
    }
  };

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setUploadingIcon(true);
    try {
      const response = await uploadApi.uploadImage(file, "badges/icons");
      setFormData((prev) => ({ ...prev, iconUrl: response.imageUrl }));
      Swal.fire("Uploaded", "Icon uploaded successfully!", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to upload icon", "error");
    } finally {
      setUploadingIcon(false);
    }
  };

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    icon: "star",
    displayOrder: 0,
    isActive: true,
    ruleType: "manual" as "manual" | "dynamic",
    dynamicType: "none" as "none" | "top_picks" | "newly_added" | "high_protein" | "order_again",
    poster: "",
    iconUrl: "",
    tagline1: "",
    tagline2: "",
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const badgesRes = await discoveryBadgesApi.getBadges();
      setBadges(badgesRes.badges || []);

      const mealsRes = await nowMealsApi.getNowMeals({ limit: 100, isActive: true });
      setNowMeals(mealsRes.nowMeals || []);
    } catch (error) {
      console.error("Failed to fetch discovery data", error);
      Swal.fire("Error", "Could not load discovery badges", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = (badge?: Badge) => {
    if (badge) {
      setEditingBadge(badge);
      setFormData({
        name: badge.name,
        code: badge.code,
        description: badge.description || "",
        icon: badge.icon,
        displayOrder: badge.displayOrder,
        isActive: badge.isActive,
        ruleType: badge.ruleType,
        dynamicType: badge.dynamicType,
        poster: badge.poster || "",
        iconUrl: badge.iconUrl || "",
        tagline1: badge.tagline1 || "",
        tagline2: badge.tagline2 || "",
      });
    } else {
      setEditingBadge(null);
      setFormData({
        name: "",
        code: "",
        description: "",
        icon: "star",
        displayOrder: badges.length + 1,
        isActive: true,
        ruleType: "manual",
        dynamicType: "none",
        poster: "",
        iconUrl: "",
        tagline1: "",
        tagline2: "",
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingBadge) {
        await discoveryBadgesApi.updateBadge(editingBadge._id, formData);
        Swal.fire("Success", "Discovery Badge updated", "success");
      } else {
        await discoveryBadgesApi.createBadge(formData);
        Swal.fire("Success", "Discovery Badge created", "success");
      }
      handleCloseModal();
      fetchData();
    } catch (error) {
      Swal.fire("Error", "Failed to save discovery badge", "error");
    }
  };

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await discoveryBadgesApi.deleteBadge(id);
        Swal.fire("Deleted!", "Discovery Badge has been deleted.", "success");
        fetchData();
      } catch (error) {
        Swal.fire("Error", "Failed to delete discovery badge", "error");
      }
    }
  };

  const handleManageMeals = (badge: Badge) => {
    setSelectedBadge(badge);
    setSelectedMealToAdd("");
    setOpenMealsModal(true);
  };

  const handleAddMealToBadge = async () => {
    if (!selectedBadge || !selectedMealToAdd) return;
    
    // Check if meal already in badge
    const mealExists = selectedBadge.meals.some((m: any) => m._id === selectedMealToAdd);
    if (mealExists) {
      Swal.fire("Note", "Meal is already added to this badge", "info");
      return;
    }

    const mealObject = nowMeals.find((m) => m._id === selectedMealToAdd);
    if (!mealObject) return;

    const updatedMeals = [...selectedBadge.meals, mealObject];
    try {
      const mealIds = updatedMeals.map((m) => m._id);
      await discoveryBadgesApi.updateBadge(selectedBadge._id, { meals: mealIds });
      
      const updatedBadge = { ...selectedBadge, meals: updatedMeals };
      setSelectedBadge(updatedBadge);
      setBadges(badges.map((b) => (b._id === selectedBadge._id ? updatedBadge : b)));
      setSelectedMealToAdd("");
      Swal.fire("Added", "Meal added to badge", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to add meal", "error");
    }
  };

  const handleRemoveMealFromBadge = async (mealId: string) => {
    if (!selectedBadge) return;

    const updatedMeals = selectedBadge.meals.filter((m: any) => m._id !== mealId);
    try {
      const mealIds = updatedMeals.map((m) => m._id);
      await discoveryBadgesApi.updateBadge(selectedBadge._id, { meals: mealIds });

      const updatedBadge = { ...selectedBadge, meals: updatedMeals };
      setSelectedBadge(updatedBadge);
      setBadges(badges.map((b) => (b._id === selectedBadge._id ? updatedBadge : b)));
      Swal.fire("Removed", "Meal removed from badge", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to remove meal", "error");
    }
  };

  const handleMoveMeal = async (index: number, direction: "up" | "down") => {
    if (!selectedBadge) return;
    
    const updatedMeals = [...selectedBadge.meals];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= updatedMeals.length) return;

    // Swap meals
    const temp = updatedMeals[index];
    updatedMeals[index] = updatedMeals[targetIndex];
    updatedMeals[targetIndex] = temp;

    try {
      const mealIds = updatedMeals.map((m) => m._id);
      await discoveryBadgesApi.updateBadge(selectedBadge._id, { meals: mealIds });

      const updatedBadge = { ...selectedBadge, meals: updatedMeals };
      setSelectedBadge(updatedBadge);
      setBadges(badges.map((b) => (b._id === selectedBadge._id ? updatedBadge : b)));
    } catch (error) {
      Swal.fire("Error", "Failed to reorder meals", "error");
    }
  };

  // Get dynamic badge matching meals for preview
  const getDynamicBadgeMealsPreview = (badge: Badge) => {
    if (badge.dynamicType === "top_picks") {
      return nowMeals.filter((m) => m.isTopPick);
    }
    if (badge.dynamicType === "newly_added") {
      return nowMeals.filter((m) => m.isNewItem);
    }
    if (badge.dynamicType === "high_protein") {
      return nowMeals.filter((m) => m.nutritionalInfo?.protein >= 15);
    }
    return [];
  };

  return (
    <Layout>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="bold">
            Discovery Badges
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
            sx={{ borderRadius: 2 }}
          >
            Create Badge
          </Button>
        </Box>

        <Card sx={{ borderRadius: 3, boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}>
          <CardContent>
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Code</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Rule Type</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Source/Type</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Icon</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Order</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {badges.map((row) => (
                      <TableRow key={row._id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                        <TableCell>{row.code}</TableCell>
                        <TableCell>
                          <span
                            style={{
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "0.75rem",
                              fontWeight: "bold",
                              backgroundColor: row.ruleType === "dynamic" ? "#e0f2fe" : "#f1f5f9",
                              color: row.ruleType === "dynamic" ? "#0369a1" : "#475569",
                            }}
                          >
                            {row.ruleType.toUpperCase()}
                          </span>
                        </TableCell>
                        <TableCell>
                          {row.ruleType === "dynamic" ? row.dynamicType.replace("_", " ") : "Manual List"}
                        </TableCell>
                        <TableCell>{row.icon}</TableCell>
                        <TableCell>{row.displayOrder}</TableCell>
                        <TableCell>
                          <Switch
                            checked={row.isActive}
                            color="success"
                            onChange={async () => {
                              try {
                                await discoveryBadgesApi.updateBadge(row._id, {
                                  isActive: !row.isActive,
                                });
                                fetchData();
                              } catch (err) {
                                Swal.fire("Error", "Could not toggle status", "error");
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleManageMeals(row)}
                            sx={{ mr: 1, borderRadius: 2 }}
                          >
                            {row.ruleType === "manual" ? "Manage Meals" : "Preview"}
                          </Button>
                          <IconButton color="primary" onClick={() => handleOpenModal(row)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton color="error" onClick={() => handleDelete(row._id)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {badges.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          No discovery badges found. Create one!
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Create / Edit Dialog */}
        <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
          <form onSubmit={handleSubmit}>
            <DialogTitle>
              {editingBadge ? "Edit Discovery Badge" : "Create Discovery Badge"}
            </DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2} mt={1}>
                <TextField
                  label="Name (e.g. Top Picks)"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <TextField
                  label="Code (unique identifier, e.g. top_picks)"
                  fullWidth
                  required
                  disabled={!!editingBadge}
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                />
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
                <TextField
                  label="Icon Name (Feather icon, e.g. star, zap, clock)"
                  fullWidth
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                />
                <TextField
                  label="Display Order"
                  type="number"
                  fullWidth
                  value={formData.displayOrder}
                  onChange={(e) =>
                    setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })
                  }
                />
                <FormControl fullWidth>
                  <InputLabel id="rule-type-label">Rule Type</InputLabel>
                  <Select
                    labelId="rule-type-label"
                    value={formData.ruleType}
                    label="Rule Type"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        ruleType: e.target.value as "manual" | "dynamic",
                        // Reset dynamicType if switching to manual
                        dynamicType: e.target.value === "manual" ? "none" : formData.dynamicType,
                      })
                    }
                  >
                    <MenuItem value="manual">Manual Selection</MenuItem>
                    <MenuItem value="dynamic">Dynamic Rules</MenuItem>
                  </Select>
                </FormControl>

                {formData.ruleType === "dynamic" && (
                  <FormControl fullWidth>
                    <InputLabel id="dynamic-type-label">Dynamic Criteria</InputLabel>
                    <Select
                      labelId="dynamic-type-label"
                      value={formData.dynamicType}
                      label="Dynamic Criteria"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          dynamicType: e.target.value as any,
                        })
                      }
                    >
                      <MenuItem value="top_picks">Top Picks (isTopPick: true)</MenuItem>
                      <MenuItem value="newly_added">Newly Added (isNewItem: true)</MenuItem>
                      <MenuItem value="high_protein">High Protein (Protein &gt;= 15g)</MenuItem>
                      <MenuItem value="order_again">Order Again (User past orders)</MenuItem>
                    </Select>
                  </FormControl>
                )}

                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    label="Poster Image URL"
                    fullWidth
                    value={formData.poster}
                    onChange={(e) => setFormData({ ...formData, poster: e.target.value })}
                    placeholder="e.g. https://domain.com/poster.png"
                  />
                  <Button variant="contained" component="label" disabled={uploadingPoster} sx={{ minWidth: 100, height: 56 }}>
                    {uploadingPoster ? <CircularProgress size={24} color="inherit" /> : "Upload"}
                    <input type="file" hidden accept="image/*" onChange={handlePosterUpload} />
                  </Button>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    label="Fallback Icon URL"
                    fullWidth
                    value={formData.iconUrl}
                    onChange={(e) => setFormData({ ...formData, iconUrl: e.target.value })}
                    placeholder="e.g. https://domain.com/icon.png"
                  />
                  <Button variant="contained" component="label" disabled={uploadingIcon} sx={{ minWidth: 100, height: 56 }}>
                    {uploadingIcon ? <CircularProgress size={24} color="inherit" /> : "Upload"}
                    <input type="file" hidden accept="image/*" onChange={handleIconUpload} />
                  </Button>
                </Box>
                <TextField
                  label="Custom Tagline Line 1 (Optional)"
                  fullWidth
                  value={formData.tagline1}
                  onChange={(e) => setFormData({ ...formData, tagline1: e.target.value })}
                  placeholder="e.g. Best Sellers,"
                />
                <TextField
                  label="Custom Tagline Line 2 (Optional)"
                  fullWidth
                  value={formData.tagline2}
                  onChange={(e) => setFormData({ ...formData, tagline2: e.target.value })}
                  placeholder="e.g. Chosen by you."
                />
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography>Active Status:</Typography>
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal}>Cancel</Button>
              <Button type="submit" variant="contained">
                Save
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Manage Meals / Preview Meals Dialog */}
        <Dialog
          open={openMealsModal}
          onClose={() => setOpenMealsModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedBadge?.ruleType === "manual"
              ? `Manage Meals inside "${selectedBadge?.name}"`
              : `Preview Meals inside "${selectedBadge?.name}"`}
          </DialogTitle>
          <DialogContent>
            {selectedBadge?.ruleType === "dynamic" ? (
              <Box>
                <Typography variant="body2" color="textSecondary" mb={2}>
                  This is a dynamically generated badge using criteria:{" "}
                  <strong>{selectedBadge.dynamicType.replace("_", " ")}</strong>
                </Typography>
                {selectedBadge.dynamicType === "order_again" ? (
                  <Typography variant="body1" align="center" sx={{ py: 4, fontStyle: "italic" }}>
                    This section resolves per user dynamically based on their individual order histories.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Meal Name</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Price</TableCell>
                          <TableCell>Protein</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getDynamicBadgeMealsPreview(selectedBadge).map((meal) => (
                          <TableRow key={meal._id}>
                            <TableCell>{meal.name}</TableCell>
                            <TableCell>{meal.categoryId?.name}</TableCell>
                            <TableCell>₹{meal.currentPrice}</TableCell>
                            <TableCell>{meal.nutritionalInfo?.protein || 0}g</TableCell>
                          </TableRow>
                        ))}
                        {getDynamicBadgeMealsPreview(selectedBadge).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                              No active meals currently match this criteria.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" gap={3}>
                <Box display="flex" gap={2} alignItems="center">
                  <FormControl fullWidth size="small">
                    <InputLabel id="add-meal-select-label">Select Meal to Add</InputLabel>
                    <Select
                      labelId="add-meal-select-label"
                      value={selectedMealToAdd}
                      label="Select Meal to Add"
                      onChange={(e) => setSelectedMealToAdd(e.target.value)}
                    >
                      {nowMeals.map((meal) => (
                        <MenuItem key={meal._id} value={meal._id}>
                          {meal.name} (₹{meal.currentPrice})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddMealToBadge}
                    sx={{ px: 3, py: 1 }}
                  >
                    Add
                  </Button>
                </Box>

                <Divider />

                <Typography variant="h6" fontWeight="bold">
                  Meals list ({selectedBadge?.meals?.length || 0} items)
                </Typography>

                <List sx={{ width: "100%", bgcolor: "background.paper", borderRadius: 2 }}>
                  {selectedBadge?.meals?.map((meal: any, index: number) => (
                    <React.Fragment key={meal._id}>
                      <ListItem>
                        <ListItemText
                          primary={meal.name}
                          secondary={`Price: ₹${meal.currentPrice} | Status: ${
                            meal.isAvailable ? "Available" : "Unavailable"
                          }`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            disabled={index === 0}
                            onClick={() => handleMoveMeal(index, "up")}
                          >
                            <ArrowUpwardIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            disabled={index === (selectedBadge?.meals?.length || 1) - 1}
                            onClick={() => handleMoveMeal(index, "down")}
                            sx={{ mr: 1 }}
                          >
                            <ArrowDownwardIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            color="error"
                            onClick={() => handleRemoveMealFromBadge(meal._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < selectedBadge.meals.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                  {(!selectedBadge?.meals || selectedBadge.meals.length === 0) && (
                    <ListItem>
                      <ListItemText
                        primary="No meals in this badge yet."
                        secondary="Select a meal above to start adding."
                        sx={{ textAlign: "center", py: 2 }}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenMealsModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default DiscoveryBadges;
